import { Location } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ALLOWED_FILES_IN_CHAT, AvailableLanguages, FILE_SIZE_LIMIT, patterns } from '@constants';
import { environment } from '@environment';
import { AllDeviceInfo, CreateMessageInMyChat, InAppNotificationType, User, UserAsset } from '@interfaces';
import { TranslateService } from '@ngx-translate/core';
import { AnalyticsService } from '@core/services/analytics.service';
import { DeviceInfoService } from '@core/services/device-info.service';
import { FileHandlerService } from '@core/services/file-handler.service';
import { MobileVisualViewportService } from '@core/services/mobile-visual-viewport.service';
import { RemoveTagsService } from '@shared/remove-tags/remove-tags.service';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';
import { ChatFacade } from '@state/chat';
import { InAppNotificationsFacade } from '@state/notifications';
import { UserAuthFacade } from '@state/user-auth';
import { isString } from 'lodash-es';
import { lastValueFrom, ReplaySubject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { ChatAttachmentsService } from '../chat-attachments.service';

@Component({
  selector: 'app-chat-mobile',
  templateUrl: './chat-mobile.component.html',
  styleUrls: ['./chat-mobile.component.scss'],
  standalone: false,
})
export class ChatMobileComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly userAuthFacade = inject(UserAuthFacade);
  private readonly translateService = inject(TranslateService);
  private readonly stackedMessages = inject(StackedMessagesService);
  private readonly inAppNotification = inject(InAppNotificationsFacade);
  private readonly chatFacade = inject(ChatFacade);
  private readonly removeTagsService = inject(RemoveTagsService);
  private readonly fileHandlerService = inject(FileHandlerService);
  private readonly deviceInfoService = inject(DeviceInfoService);
  private readonly location = inject(Location);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly mobileVisualViewportService = inject(MobileVisualViewportService);
  private readonly chatAttachmentsService = inject(ChatAttachmentsService);

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @ViewChild('chatMessageInput') chatMessageInput: ElementRef<HTMLTextAreaElement>;
  @ViewChild('chatMessagesContainer') chatMessagesContainer: ElementRef<HTMLElement>;
  @ViewChild('fileUploadInput') fileUploadInput: ElementRef<HTMLElement>;

  @ViewChildren('chatMessage') chatMessages!: QueryList<ElementRef<HTMLElement>>;

  public myself: User;

  public showLoader = true;

  public currentLang: AvailableLanguages;

  public loading$ = this.chatFacade.loading$;
  public chatMessages$ = this.chatFacade.chatMessages$;

  public deviceInfo: AllDeviceInfo;

  public attachmentsToAdd$ = this.chatFacade.attachmentsToAdd$.pipe(
    filter(items => !!items),
    map(items => this.chatAttachmentsService.mapAttachments(items))
  );

  public newMessageContent = '';
  public messageAttachments: string[] = [];
  public uploadingAttachments = false;
  public hasAttachmentsToAdd = false;
  private hasInitialScrollDone = false;
  private showLoaderTimeout: ReturnType<typeof setTimeout> | null = null;

  public API_URL = environment.API_URL;

  public async ngOnInit(): Promise<void> {
    this.mobileVisualViewportService.activate();
    this.deviceInfo = this.deviceInfoService.getInfo();

    this.deviceInfoService.infoEmitter.pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.deviceInfo = info;
    });

    this.currentLang = this.translateService.currentLang as AvailableLanguages;

    this.userAuthFacade.myself$
      .pipe(
        filter(myself => !!myself?.id),
        takeUntil(this.destroy$)
      )
      .subscribe(async myself => {
        this.myself = myself;
      });

    this.userAuthFacade.myself$
      .pipe(
        filter(myself => !!myself?.id),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.chatFacade.getMyChat$().pipe(take(1), takeUntil(this.destroy$)).subscribe();
      });

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(({ lang: langKey }) => {
      this.currentLang = langKey as AvailableLanguages;
    });

    this.toggleChatButtonVisibility(false);
    this.analyticsService.trackEvent('user_event', 'user_opened_chat');
  }

  public async ngAfterViewInit(): Promise<void> {
    this.chatFacade.addChatMessageSuccess$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.newMessageContent = '';
    });

    this.attachmentsToAdd$.pipe(takeUntil(this.destroy$)).subscribe(attachments => {
      this.hasAttachmentsToAdd = !!attachments?.length;
      if (!attachments?.length) {
        this.messageAttachments = [];
        this.uploadingAttachments = false;
        return;
      }

      this.uploadingAttachments = attachments.some(attachment => attachment.loading);
      this.messageAttachments = this.chatAttachmentsService.getUploadedAttachmentIds(attachments);
    });
    this.inAppNotification.newNotificationReceived$.pipe(takeUntil(this.destroy$)).subscribe(({ notification }) => {
      // interested only in this type
      if (InAppNotificationType.EMPLOYEE_SENT_MESSAGE_IN_CHAT !== notification.type) {
        return;
      }

      this.chatFacade.getMyChat();
    });

    this.chatMessages.changes.pipe(takeUntil(this.destroy$)).subscribe(messages => {
      if (!messages.length) {
        return;
      }

      if (!this.hasInitialScrollDone) {
        this.hasInitialScrollDone = true;
        queueMicrotask(() => this.scrollToBottom('auto'));
        return;
      }

      queueMicrotask(() => this.scrollToBottom());
    });

    this.showLoaderTimeout = setTimeout(() => {
      this.showLoader = false;
      this.showLoaderTimeout = null;
    });
  }

  public ngOnDestroy(): void {
    this.clearShowLoaderTimeout();
    this.destroy$.next(true);
    this.destroy$.complete();
    this.mobileVisualViewportService.deactivate();

    this.toggleChatButtonVisibility(true);
  }

  public async uploadFiles($event: any): Promise<void> {
    const files = $event.target.files as FileList;
    const validatedFiles = [];
    const invalidFiles = [];
    const MAX_NUMBER_OF_FILES = 10;

    const existingItems = await lastValueFrom(this.attachmentsToAdd$.pipe(take(1), takeUntil(this.destroy$)));
    const selectedFileNames = new Set<string>();

    if (files.length > MAX_NUMBER_OF_FILES) {
      this.stackedMessages.showWarning({
        message: 'COMMON.VALIDATOR.YOU_CAN_UPLOAD_MAX_10_AT_A_TIME',
        duration: 4500,
      });
    }

    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      // If user managed to upload some wierd file
      // Let him - we don't care that app is not working now - his fault.
      if (!file?.size || !file?.type) {
        this.stackedMessages.showError({
          message: 'CHAT.CANNOT_READ_FILE_CORRECTLY',
          translateParams: { fileName: file.name },
          duration: 6500,
        });
        invalidFiles.push(file);
        continue;
      }

      if (file.size > FILE_SIZE_LIMIT) {
        this.stackedMessages.showError({
          message: 'CHAT.FILE_NOT_ADDED_BECAUSE_TOO_BIG',
          translateParams: { fileName: file.name },
          duration: 6500,
        });
        invalidFiles.push(file);
        continue;
      }

      if (!ALLOWED_FILES_IN_CHAT.includes(file.type.toLowerCase())) {
        this.stackedMessages.showError({
          message: 'CHAT.FILE_NOT_ADDED_BECAUSE_INVALID',
          translateParams: { fileName: file.name },
          duration: 6500,
        });
        invalidFiles.push(file);
        continue;
      }

      const fileWithSuchNameAlreadyAdded = existingItems?.find(
        ({ fileOriginalName }) => fileOriginalName === file.name
      );
      if (fileWithSuchNameAlreadyAdded || selectedFileNames.has(file.name)) {
        continue;
      }

      selectedFileNames.add(file.name);
      validatedFiles.push(file);

      // max 10 at a time
      if (validatedFiles.length === 10) {
        break;
      }
    }

    if (invalidFiles.length) {
      this.stackedMessages.showError({
        message: 'CHAT.SOME_OF_THE_FILES_NOT_ADDED',
        duration: 8500,
      });
    }

    (this.fileUploadInput.nativeElement as any).value = '';
    if (!validatedFiles.length) {
      return;
    }

    validatedFiles.forEach(validatedFile => {
      this.chatFacade.uploadMessageAttachments({
        files: [{ file: validatedFile, fileOriginalName: validatedFile.name }],
      });
    });
  }

  public downloadMessageAttachment(messageId: string, attachmentId: string, fileOriginalName?: string | null): void {
    const fileExtension = this.getFileExtension(fileOriginalName);
    const previewTargetKey = this.fileHandlerService.canOpenInBrowser(fileExtension, null)
      ? this.fileHandlerService.preparePreviewTarget(attachmentId)
      : undefined;

    this.chatFacade.downloadAttachmentFromChat(messageId, attachmentId, previewTargetKey);
  }

  private getFileExtension(fileOriginalName?: string | null): string {
    if (!fileOriginalName) {
      return '';
    }

    const lastDotIndex = fileOriginalName.lastIndexOf('.');

    return lastDotIndex >= 0 ? fileOriginalName.slice(lastDotIndex) : '';
  }

  public removeAsset(userAsset: UserAsset): void {
    this.chatFacade.removeAttachmentFromUploading({
      fileOriginalName: userAsset.fileOriginalName,
      userAssetId: userAsset.id,
    });
  }

  public submitMessage($event: any): void {
    let content = this.newMessageContent;
    const isKeyboardSubmit = $event instanceof KeyboardEvent;

    if (isKeyboardSubmit && this.deviceInfo?.deviceTypeDetected !== 'DESKTOP') {
      return;
    }

    if (($event instanceof KeyboardEvent || $event instanceof MouseEvent) && $event.shiftKey) {
      return;
    }

    $event.preventDefault();

    if (this.uploadingAttachments) {
      return;
    }

    if (content?.length) {
      content = content.trim();
      content = this.removeTagsService.removeTags(content, ['h1', 'h2', 'h3', 'br', 'b', 'i', 'li', 'ul', 'ol']);
      if (content.length === 0 && this.messageAttachments.length === 0) {
        this.stackedMessages.showError({
          message: 'CHAT.WRONG_INPUT',
          duration: 3000,
        });
      }
    }
    if (this.messageAttachments.length === 0 && (!content || !this.messageContentValid(this.newMessageContent))) {
      return;
    }

    const opts: CreateMessageInMyChat = {
      content,
      attachments: [...this.messageAttachments],
    };

    this.chatFacade.addChatMessage({ ...opts });
    this.analyticsService.trackEvent('user_event', 'user_sent_chat_message');
  }

  public onChatMessageInputFocus(): void {
    this.scrollToBottom('auto');
  }

  public scrollToBottom(behavior: ScrollBehavior = 'smooth'): void {
    const htmlElem = this.chatMessagesContainer?.nativeElement;

    if (!htmlElem) {
      return;
    }

    htmlElem.scrollTo({
      top: htmlElem.scrollHeight,
      behavior: this.resolveScrollBehavior(behavior),
    });

    if (this.myself?.id) {
      this.inAppNotification.markAllOfTypeAsRead({
        inAppNotificationType: InAppNotificationType.EMPLOYEE_SENT_MESSAGE_IN_CHAT,
        relatedForeignerId: this.myself.id,
      });
    }
  }

  public messageContentValid(content: string): boolean {
    const pattern = patterns.CommentTextValidator;

    if (!isString(content)) {
      return false;
    }
    return !!(content as string).match(pattern);
  }

  public openFilePicker(): void {
    const input = this.fileUploadInput?.nativeElement as (HTMLInputElement & { showPicker?: () => void }) | undefined;

    if (!input) {
      return;
    }

    if (typeof input.showPicker === 'function') {
      input.showPicker();
      return;
    }

    input.click();
  }

  public toggleChatButtonVisibility(status: boolean): void {
    const chatWrappers = document.getElementsByClassName('chat-button-wrapper');
    if (chatWrappers?.length !== 1) {
      return;
    }
    const chatWrapper = chatWrappers[0] as HTMLElement;
    if (status) {
      chatWrapper.style.display = 'inline';
    } else {
      chatWrapper.style.display = 'none';
    }
  }

  public goBack(): void {
    if (this.uploadingAttachments) {
      return;
    }

    this.location.back();
  }

  public canDeactivate(): boolean {
    return !this.uploadingAttachments;
  }

  private resolveScrollBehavior(behavior: ScrollBehavior): ScrollBehavior {
    if (behavior !== 'smooth' || this.deviceInfo?.deviceTypeDetected === 'DESKTOP') {
      return behavior;
    }

    return this.chatMessageInputIsFocused() ? 'auto' : behavior;
  }

  private chatMessageInputIsFocused(): boolean {
    return this.chatMessageInput?.nativeElement === document.activeElement;
  }

  private clearShowLoaderTimeout(): void {
    if (!this.showLoaderTimeout) {
      return;
    }

    clearTimeout(this.showLoaderTimeout);
    this.showLoaderTimeout = null;
  }
}
